import { useMemo } from "react"
import { useWidget } from "../../../providers/widget"
import { ExploreWidgetConfig } from "../config"
import { buildSearchbarCss } from "./searchbarCss"
import { buildCardSkinCss } from "../../../common/property-card/card-skin-css"
import { safeCssColor, safeCssFontFamily } from "../../../common/css-safe"
import { hostFontSizeCss } from "../../../common/host-scope"
import { ELEVATED_TOKEN_SCOPE } from "./elevatedScope"

const clampSplit = (v: number | null | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(70, Math.max(20, Math.round(v))) : null

// The elevated-portal token scope class (#943) lives in its own module
// (#1259) so searchbarCss.ts can emit onto it without importing this file
// back; the existing import sites keep resolving it from here.
export { ELEVATED_TOKEN_SCOPE }

/**
 * Toolkit map-module port (#852): viewport-fit sticky sizing and per-breakpoint
 * map/grid split, generated as CSS overriding explorer.css's desktop defaults
 * (sticky top:0/height:100vh and the 45%/55% split). Desktop-only (960px+),
 * matching both the base stylesheet's map breakpoint and the toolkit's
 * desktop-only guard; below 960px the map is stacked and none of this applies —
 * which is also the mobile/hide "cleanup": these rules simply stop matching.
 * Pure CSS on purpose — no JS sizing, so there is nothing to freeze or unwind
 * during results loading (the toolkit's JS-measurement safeguards are moot).
 */
export function buildMapLayoutCss(map: ExploreWidgetConfig['map'] | undefined): string {
  if (!map) return ''
  const parts: string[] = []

  const vf = map.viewportFit
  if (vf?.enabled) {
    const top = typeof vf.topOffsetPx === 'number' ? vf.topOffsetPx : 0
    const bottom = typeof vf.bottomGapPx === 'number' ? vf.bottomGapPx : 0
    parts.push(`
    @media (min-width: 960px) {
      .hfse.with-map.map-visible .hfse-map-container {
        top: ${top}px;
        height: calc(100vh - ${top + bottom}px);
        min-height: 320px;
      }
    }`)
  }

  const splits: Array<[string, number | null]> = [
    ['(min-width: 960px) and (max-width: 1199px)', clampSplit(map.splitPct?.md)],
    ['(min-width: 1200px) and (max-width: 1599px)', clampSplit(map.splitPct?.lg)],
    ['(min-width: 1600px)', clampSplit(map.splitPct?.xl)],
  ]
  for (const [media, pct] of splits) {
    if (pct === null) continue
    parts.push(`
    @media ${media} {
      .hfse.with-map.map-visible .hfse-sidebar { width: ${pct}%; }
      .hfse.with-map.map-visible .hfse-body { width: ${100 - pct}%; }
    }`)
  }

  return parts.join('\n')
}

/**
 * Loose hex parser for #896: accepts `#rgb`, `#rgba`, `#rrggbb`, and
 * `#rrggbbaa` — but a value carrying an alpha channel only when it is FULLY
 * opaque (`ff` / `f`, what the color picker writes for an opaque pick).
 *
 * The alpha pair is NOT cosmetic. `!important` on the `.bg-accent` fill is
 * cascade priority, not opacity — `background-color: #4f7fe780 !important`
 * still computes to rgba(79,127,231,0.5) and composites over whatever sits
 * behind the button (the dark `cardBackground`, not the accent triplet), so
 * a contrast pick scored against the bare triplet is wrong under alpha:
 * at 0.5 it picked black at 2.53:1 where the pre-existing `#fff` read
 * 8.30:1 against the real composite. And NO near-opaque band is safe
 * either — at alpha 0.902 a crossover-luminance accent (`#767676e6`) still
 * picks black from the bare triplet (4.62:1) while the true composite over
 * `#1a1a1a` reads only 4.06:1 (#901 codex r1). So only fully-opaque input
 * derives; anything else returns `null` and the caller keeps explorer.css's
 * `#fff` — the exact pre-fix rendering, which this fix must never make
 * worse.
 *
 * ACCEPTED LIMITATION (codex fresh pass): a NEAR-opaque alpha (`fe`, 0.996)
 * also refuses, staying on the pre-fix `#fff` instead of being fixed. By
 * design: without compositing there is no sound acceptance threshold (some
 * accent sits at the 4.5 crossover for every cutoff), and a sound composite
 * path needs PER-SURFACE derivation — the two covered surfaces sit on
 * different backdrops (cardBackground vs background). The color picker
 * writes `ff` for opaque, so the refused band is hand-authored input only.
 *
 * Deliberately separate from `searchbarCss.ts`'s `hexToRgb`: that parser is
 * intentionally 3/6-digit-only for SBP's `accentColor` contract, and the
 * color picker backing `colors.accent` can save 8-digit output (#896 notes
 * this exact trap), so widening the shared one would change SBP parsing too.
 */
export function hexToRgbLoose(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.exec(hex.trim())
  if (!m) return null
  let h = m[1]
  if (h.length <= 4) h = h.split('').map((c) => c + c).join('')
  if (h.length === 8 && parseInt(h.slice(6, 8), 16) !== 0xff) return null
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  }
}

/** WCAG 2.1 §1.4.3 relative luminance for one sRGB channel (0-255). */
function srgbChannelToLinear(c: number): number {
  const s = c / 255
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
}

/** WCAG 2.1 §1.4.3 relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance({ r, g, b }: { r: number; g: number; b: number }): number {
  return 0.2126 * srgbChannelToLinear(r) + 0.7152 * srgbChannelToLinear(g) + 0.0722 * srgbChannelToLinear(b)
}

/** WCAG 2.1 §1.4.3 contrast ratio between two relative luminances: 1-21. */
export function contrastRatio(luminanceA: number, luminanceB: number): number {
  const lighter = Math.max(luminanceA, luminanceB)
  const darker = Math.min(luminanceA, luminanceB)
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * #896 option A: pick whichever of pure black/white clears WCAG AA (4.5:1)
 * against `hex`, choosing the higher-contrast one. Binary black/white only —
 * a softened ink (e.g. `#111827`) can itself dip under 4.5:1 at the accent
 * gamut's crossover luminance (#896's own evidence table: `#222222` already
 * fails one shipping light accent at 4.18:1), so there is no safe third
 * choice without per-accent QA. Returns `null` when `hex` doesn't parse so
 * the caller falls back to the existing `#fff` in `explorer.css` instead of
 * emitting a bogus override.
 */
export function pickAccentForegroundDark(hex: string): '#000000' | '#ffffff' | null {
  const rgb = hexToRgbLoose(hex)
  if (!rgb) return null
  const accentLum = relativeLuminance(rgb)
  const vsWhite = contrastRatio(accentLum, 1)
  const vsBlack = contrastRatio(accentLum, 0)
  return vsBlack >= vsWhite ? '#000000' : '#ffffff'
}

// One warn per offending accent value per process lifetime — module-level so
// SSR request storms and StrictMode double-invokes cannot spam the log. Keys
// are pre-truncated and the set is size-capped so a hostile stream of unique
// multi-megabyte "accents" can neither spam nor accumulate (#901 codex r2).
const warnedAccents = new Set<string>()
const WARNED_ACCENTS_MAX = 32

/**
 * Dark-palette overrides for the auto FIRST-PAINT path (#900). The base
 * template below always emits the LIGHT palette (so light output stays
 * byte-identical); this appends the DARK palette under two selector prefixes:
 *   - `:host([data-hr-theme="dark"])` — the runtime marker (explicit dark +
 *     post-hydration auto), and the mirrored elevated-dialog host.
 *   - inside `@media (prefers-color-scheme: dark)`,
 *     `:host([data-hr-scheme="auto"]:not([data-hr-theme]))` — a server-stamped
 *     auto widget on a dark OS, painting dark AT PARSE with no JS/cookie; the
 *     `:not` (which MUST stay INSIDE `:host(...)` — outside it silently never
 *     matches the host) drops it the instant the runtime stamps the marker at
 *     hydration. Both prefixes arrive `:where(...)`-wrapped so they add zero
 *     specificity: every override keeps the exact selector weight the old
 *     resolvedTheme-gated dark branch had, preserving the static-sheet and
 *     customCss cascade relationships.
 * Mirrors the `resolvedTheme === 'dark'` branch of the template so both paths
 * render identically. `:host(...)` (not a descendant `[data-hr-theme]`) is
 * required to reach the shadow host from inside its own stylesheet.
 */
function explorerDarkOverrides(
  prefix: string,
  dc: ExploreWidgetConfig['darkModeColors'],
  darkAccentFg: '#000000' | '#ffffff' | null,
): string {
  // #904: every dc.* token is admin-supplied (bare z.string()) and interpolated
  // into a <style> via dangerouslySetInnerHTML, so each is wrapped in
  // safeCssColor at emission — an injection payload validates to "" and degrades
  // to the stylesheet default instead of breaking out of the element.
  const container = `${prefix} .hfse-explorer-container`
  const dialog = `${prefix} .hr-dialog-content`
  const elevated = `${prefix} .${ELEVATED_TOKEN_SCOPE}`
  return `
    ${container} {
      color: ${safeCssColor(dc.foreground)};
      color-scheme: dark;
    }
    ${container}, ${dialog}, ${elevated} {
      --hfse-accent: ${safeCssColor(dc.accent)};
      --hfse-foreground: ${safeCssColor(dc.foreground)};
      --hfse-muted-foreground: ${safeCssColor(dc.mutedForeground)};
      --hfse-background: ${safeCssColor(dc.background)};
      --hfse-card-bg: ${safeCssColor(dc.cardBackground)};
      --hfse-card-fg: ${safeCssColor(dc.cardForeground)};
      --hfse-input-bg: ${safeCssColor(dc.inputBackground)};
      --hfse-input-fg: ${safeCssColor(dc.inputForeground)};
      --hfse-input-placeholder: ${safeCssColor(dc.inputPlaceholder)};
      --hfse-border: ${safeCssColor(dc.borderColor)};
      --hfse-btn-bg: ${safeCssColor(dc.buttonBackground)};
      --hfse-btn-fg: ${safeCssColor(dc.buttonForeground)};
    }
    ${dialog} {
      background: ${safeCssColor(dc.cardBackground)};
      color: ${safeCssColor(dc.foreground)};
      color-scheme: dark;
    }
    ${dialog} .hk-modal-footer {
      border-top-color: ${safeCssColor(dc.borderColor)};
    }
    ${dc.accent ? `
    ${prefix} .text-accent {
      color: ${safeCssColor(dc.accent)} !important;
    }
    ${prefix} .bg-accent {
      background-color: ${safeCssColor(dc.accent)} !important;
    }
    ${prefix} .hfse-rdp, ${dialog} .hfse-rdp {
      --rdp-accent: ${safeCssColor(dc.accent)};
      --rdp-accent-light: color-mix(in srgb, ${safeCssColor(dc.accent)} 15%, transparent);
    }
    ` : ''}
    ${darkAccentFg ? `
    ${prefix} .hk-button.bg-accent,
    ${prefix} .hfse-form .hfse-form-additional-filters-btn {
      color: ${darkAccentFg};
    }
    ` : ''}
    ${prefix} .hfse-rdp, ${dialog} .hfse-rdp {
      --dp-range-bg: rgba(255, 255, 255, 0.14);
      --dp-hover-range-bg: rgba(255, 255, 255, 0.14);
      --dp-endpoint-bg: #f3f4f6;
      --dp-endpoint-color: #1a1a1a;
      --dp-range-color: ${safeCssColor(dc.foreground)};
      --dp-cell-hover-bg: rgba(255, 255, 255, 0.08);
      --dp-nav-border: rgba(255, 255, 255, 0.18);
      --dp-nav-disabled-color: rgba(255, 255, 255, 0.32);
      --dp-nav-disabled-border: rgba(255, 255, 255, 0.10);
      --dp-scrollbar-thumb: rgba(255, 255, 255, 0.32);
      --dp-weekday-color: #9a9a9a;
      --dp-disabled-color: #8c8c8c;
    }
    ${prefix} .hfse-form-guests-options {
      --gp-row-border: rgba(255, 255, 255, 0.12);
      --gp-btn-border: rgba(255, 255, 255, 0.35);
      --gp-btn-color: rgba(255, 255, 255, 0.78);
      --gp-btn-hover: #f3f4f6;
      --gp-hint-color: #9a9a9a;
    }
    /* #964: no dark-mode override for .hfse-form-guests-options
       .hfse-modal-next-button here — it was unscoped to the pro-only
       .hfse-sbp-guests-dress class, so it also matched the CLASSIC guests
       modal's and BedroomsField's Apply/Done buttons (neither ever carries
       the dress class) and, at equal specificity, beat the .hk-button.bg-accent
       rule above that paints the derived WCAG-contrast color (#896) with the
       generic dc.foreground token. For the DRESS-scoped pro modal/sheet this
       rule was a no-op anyway: explorer.css:4124
       (.hfse-sbp-guests-dress.hfse-form-guests-options .hfse-modal-next-button)
       already sets color: var(--hfse-foreground, #111827), and --hfse-foreground
       resolves to this same dc.foreground token via the --hfse-* block above —
       so dropping it changes nothing for the dress-scoped case and restores
       the derived contrast color for the classic/BedroomsField case. */
  `
}

export function useExplorerCss(): string {
  const { options, resolvedTheme, widgetId } = useWidget<ExploreWidgetConfig>()

  const colors = useMemo(() => {
    return resolvedTheme === 'dark' ? options.darkModeColors : options.lightModeColors
  }, [resolvedTheme, options.lightModeColors, options.darkModeColors])

  // #896 option A: dark-only accent-label contrast fix. `resolvedTheme` gates
  // this at the React level (same mechanism the dark datepicker-neutral
  // block below already relies on for #870) — the memo is `null` on every
  // light render, so light output is byte-identical by construction, not by
  // a CSS selector that could theoretically leak. See PR for the ownership
  // evidence and why light stays untouched by design.
  const darkAccentForeground = useMemo(() => {
    if (resolvedTheme !== 'dark' || !colors.accent) return null
    const picked = pickAccentForegroundDark(colors.accent)
    const warnKey = String(colors.accent).slice(0, 64)
    if (!picked && !warnedAccents.has(warnKey) && warnedAccents.size < WARNED_ACCENTS_MAX) {
      // Surfaced rather than silent (#901 review): a non-hex accent (the
      // picker field is a bare z.string(), so rgb()/hsl()/named/var() all
      // reach here) or a translucent one means the a11y derivation did not
      // apply and the label stays explorer.css's #fff. Deduped on the
      // truncated key (the memo re-fires per mount/SSR request and
      // StrictMode double-invokes it) so free-text admin input can neither
      // spray multiline noise into the log nor be retained at full length.
      warnedAccents.add(warnKey)
      console.warn(
        `[hr-explorer] accent ${JSON.stringify(warnKey)} is not a fully-opaque hex color; ` +
        'dark accent-label contrast derivation skipped, label stays #fff',
      )
    }
    return picked
  }, [resolvedTheme, colors.accent])

  // Accent-label contrast pick for the DARK palette, computed regardless of the
  // resolved theme — the appended dark overrides ship on every render (auto
  // first-paint can't wait for resolvedTheme to flip). The warn path stays on
  // `darkAccentForeground` above so a bad accent is surfaced exactly once.
  const appendedDarkAccentFg = useMemo(
    () => (options.darkModeColors.accent ? pickAccentForegroundDark(options.darkModeColors.accent) : null),
    [options.darkModeColors.accent],
  )

  // NOTE: the --hfse-* token block and base font rule below are mirrored (in
  // scoped form) by buildPreviewCss in common/property-card/skin-preview.tsx —
  // the dashboard's Card Template preview. Token additions/renames here must
  // land there too, or the preview drifts from what the widget renders.
  return useMemo(() => `
    .hfse *, .hr-dialog-content * {
      font-family: '${safeCssFontFamily(options.font.family)}' !important;
    }
    ${hostFontSizeCss(widgetId, options.font.size)}
    .hfse-explorer-container {
      color: ${safeCssColor(colors.foreground)};
      ${resolvedTheme === 'dark' ? 'color-scheme: dark;' : ''}
    }
    .hfse-explorer-container, .hr-dialog-content, .${ELEVATED_TOKEN_SCOPE} {
      --hfse-accent: ${safeCssColor(colors.accent)};
      --hfse-foreground: ${safeCssColor(colors.foreground)};
      --hfse-muted-foreground: ${safeCssColor(colors.mutedForeground)};
      --hfse-background: ${safeCssColor(colors.background)};
      --hfse-card-bg: ${safeCssColor(colors.cardBackground)};
      --hfse-card-fg: ${safeCssColor(colors.cardForeground)};
      --hfse-input-bg: ${safeCssColor(colors.inputBackground)};
      --hfse-input-fg: ${safeCssColor(colors.inputForeground)};
      --hfse-input-placeholder: ${safeCssColor(colors.inputPlaceholder)};
      --hfse-border: ${safeCssColor(colors.borderColor)};
      --hfse-btn-bg: ${safeCssColor(colors.buttonBackground)};
      --hfse-btn-fg: ${safeCssColor(colors.buttonForeground)};
    }
    .hr-dialog-content {
      background: ${safeCssColor(colors.cardBackground)};
      color: ${safeCssColor(colors.foreground)};
      ${resolvedTheme === 'dark' ? 'color-scheme: dark;' : ''}
    }
    .hr-dialog-content .hk-modal-footer {
      border-top-color: ${safeCssColor(colors.borderColor)};
    }
    ${colors.accent ? `
    .text-accent {
      color: ${safeCssColor(colors.accent)} !important;
    }
    .bg-accent {
      background-color: ${safeCssColor(colors.accent)} !important;
    }
    .hfse-rdp, .hr-dialog-content .hfse-rdp {
      --rdp-accent: ${safeCssColor(colors.accent)};
      --rdp-accent-light: color-mix(in srgb, ${safeCssColor(colors.accent)} 15%, transparent);
    }
    ` : ''}
    ${resolvedTheme === 'dark' ? `
    ${darkAccentForeground ? `
    /* #896 option A: the accent FILL above is theme-aware; explorer.css:548
       hardcodes the Apply-button LABEL to #fff, which only reads correctly
       against light/mid accents. This override reaches both the in-shadow
       tree (this <style> renders after the static explorer.css <link>) and
       elevated filter dialogs (DialogContent's cssString is appended after
       the cloned stylesheet link in elevated-portal.tsx) via source-order,
       same mechanism the dark datepicker-neutral rule below already uses.
       Nested inside this same resolvedTheme==='dark' branch (rather than a
       sibling interpolation) so light output is BYTE-identical — not just
       functionally unchanged: the whole branch, whitespace included, only
       ever concatenates into the string when dark.
       The additional-filters trigger is the same defect on the same accent
       var (#896 lists it under the same heading): explorer.css:330 paints
       color:#fff on var(--hfse-accent, #b8986a) at the same (0,2,0)
       specificity, so this later <style> wins on source order there too.
       Note .hk-button.bg-accent also matches the legacy search button
       (FilterBar.tsx renders it with both classes) — deliberate, same
       fill-vs-label defect, disclosed in the PR body. */
    .hk-button.bg-accent,
    .hfse-form .hfse-form-additional-filters-btn {
      color: ${darkAccentForeground};
    }
    ` : ''}
    /* Datepicker neutrals inverted for dark hosts (#870). explorer.css carries
       the toolkit's light palette verbatim, which assumes a white dialog: on
       the dark dialog background those values collapse — a #222222 endpoint
       circle on #1a1a1a is ~1.08:1, and the #f3f4f6 in-range ribbon paints a
       near-white block through the middle of a dark panel. Inverting the two
       neutrals keeps the reference's shape (solid endpoint, flat neutral
       ribbon, no accent fill) while restoring contrast. */
    .hfse-rdp, .hr-dialog-content .hfse-rdp {
      --dp-range-bg: rgba(255, 255, 255, 0.14);
      --dp-hover-range-bg: rgba(255, 255, 255, 0.14);
      --dp-endpoint-bg: #f3f4f6;
      --dp-endpoint-color: #1a1a1a;
      --dp-range-color: ${safeCssColor(colors.foreground)};
      --dp-cell-hover-bg: rgba(255, 255, 255, 0.08);
      --dp-nav-border: rgba(255, 255, 255, 0.18);
      --dp-nav-disabled-color: rgba(255, 255, 255, 0.32);
      --dp-nav-disabled-border: rgba(255, 255, 255, 0.10);
      --dp-scrollbar-thumb: rgba(255, 255, 255, 0.32);
      /* 8/6 a11y fix (Eli decision): explorer.css's light fallbacks for these
         two are #5f6368 / #757575; on the dark dialog the compliant pair is
         #9a9a9a (6.19:1 on #1a1a1a) for the weekday header and #8c8c8c
         (5.18:1) for disabled-day numerals — both still visibly secondary
         next to full-strength text. Pinned by datepicker-contrast-a11y.test. */
      --dp-weekday-color: #9a9a9a;
      --dp-disabled-color: #8c8c8c;
    }
    ` : ''}
    /* Dark palette for the auto first-paint path (#900) plus the runtime
       marker. The base rules above always emit LIGHT; these override only under
       dark selectors, so light rendering is byte-identical to before. */
    ${explorerDarkOverrides(':where(:host([data-hr-theme="dark"]))', options.darkModeColors, appendedDarkAccentFg)}
    @media (prefers-color-scheme: dark) {
      ${explorerDarkOverrides(':where(:host([data-hr-scheme="auto"]:not([data-hr-theme])))', options.darkModeColors, appendedDarkAccentFg)}
    }
    ${buildMapLayoutCss(options.map)}
    ${buildSearchbarCss(options)}
    ${buildCardSkinCss(options.card)}
    ${options.customCss ?? ''}
  `, [widgetId, options.font.family, options.font.size, colors, resolvedTheme, darkAccentForeground, appendedDarkAccentFg, options.darkModeColors, options.customCss, options.map, options.searchbar, options.stickyFilter, options.card])
}
